import { z } from "zod"

export const ORDER_COST_SOURCE = "magento_order" as const
export const ORDER_COST_MAX_ITEMS = 200
export const ORDER_COST_MAX_INVOICES = 1000
export const ORDER_COST_MAX_BYTES = 256 * 1024
export const orderCostDecimalSchema = z.string().regex(/^-?\d{1,16}(?:\.\d{1,4})?$/)
const sourceId = z.string().regex(/^\d+$/).max(20)
const sourceTime = z.iso.datetime()

/** Retained source facts, never monetary certification or a product timeline. */
export const orderCostSourceSchema = z.object({
  source: z.literal(ORDER_COST_SOURCE),
  order: z.object({
    entityId: sourceId,
    incrementId: z.string().min(1).max(100),
    storeId: sourceId,
    marketplaceOrderId: z.string().max(200).nullable(),
    createdAt: sourceTime,
    state: z.string().max(100),
    status: z.string().max(100),
    baseCurrency: z.string().regex(/^[A-Z]{3}$/),
    replacementEntityId: sourceId.nullable(),
  }),
  items: z
    .array(
      z.object({
        itemId: sourceId,
        productId: sourceId.nullable(),
        sku: z.string().min(1).max(200),
        productType: z.string().max(100),
        parentItemId: sourceId.nullable(),
        qtyOrdered: orderCostDecimalSchema,
        qtyCanceled: orderCostDecimalSchema,
        qtyRefunded: orderCostDecimalSchema,
        baseCost: orderCostDecimalSchema.nullable(),
        // Optional only so immutable snapshots captured before recovery stay readable.
        basePrice: orderCostDecimalSchema.nullable().optional(),
        baseRowTotal: orderCostDecimalSchema.nullable().optional(),
        baseTaxAmount: orderCostDecimalSchema.nullable().optional(),
        pack: z
          .object({
            productId: sourceId,
            unitsPerPack: orderCostDecimalSchema,
            simpleSku: z.string().min(1).max(200),
            requestedPacks: orderCostDecimalSchema,
            product: z.object({ entityId: sourceId, sku: z.string().min(1).max(200) }).nullable(),
          })
          .nullable()
          .optional(),
        backfill: z
          .object({
            oldCost: orderCostDecimalSchema.nullable(),
            newCost: orderCostDecimalSchema.nullable(),
            createdAt: sourceTime,
          })
          .nullable(),
      })
    )
    .max(ORDER_COST_MAX_ITEMS),
  invoices: z
    .array(
      z.object({
        entityId: sourceId,
        invoiceId: sourceId,
        orderItemId: sourceId,
        productId: sourceId.nullable(),
        sku: z.string().max(200),
        qty: orderCostDecimalSchema,
        baseCost: orderCostDecimalSchema.nullable(),
        createdAt: sourceTime,
      })
    )
    .max(ORDER_COST_MAX_INVOICES),
  placement: z
    .object({
      entityId: sourceId,
      cost: orderCostDecimalSchema.nullable(),
      createdAt: sourceTime,
    })
    .nullable(),
})
export type OrderCostSource = z.infer<typeof orderCostSourceSchema>
export const ORDER_COST_FAILURE = {
  unavailable: "order_cost_source_unavailable",
  invalid: "order_cost_source_invalid",
  oversized: "order_cost_source_oversized",
  identity: "order_cost_source_identity_mismatch",
} as const
export const orderCostReadFailureSchema = z.object({
  failure: z.enum(ORDER_COST_FAILURE),
})
export type OrderCostReadFailure = z.infer<typeof orderCostReadFailureSchema>
export type OrderCostRead = OrderCostSource | OrderCostReadFailure

export const orderCostSnapshotSchema = z
  .object({
    version: z.literal(1),
    selectedEntityId: sourceId,
    selectionVersion: z.number().int().nonnegative(),
    observedAt: sourceTime,
    recordedAt: sourceTime,
    checkedAt: sourceTime,
    fingerprint: z.string().length(64),
    evidence: orderCostSourceSchema.nullable(),
    failure: z.enum(ORDER_COST_FAILURE).nullable(),
  })
  .refine(
    (value) => value.failure === null || value.evidence === null,
    "Failed order cost evidence cannot contain financial source facts"
  )
export type OrderCostSnapshot = z.infer<typeof orderCostSnapshotSchema>

export const PNL_SOURCE_BASIS = {
  product: "product_observation",
  order: "order_linked",
} as const
export const PNL_ORDER_COST_POLICY = "marketplace-sale-order-linked-estimate-v1" as const

/** Explicit approval for a captured catalog estimate, never historical source replacement. */
export const orderCatalogEstimateSchema = z.object({
  orderId: z.string().min(1).max(200),
  orderEntityId: sourceId,
  itemId: sourceId,
  productId: sourceId,
  simpleSku: z.string().min(1).max(200),
  quantity: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  originalCost: orderCostDecimalSchema.refine((value) => Number(value) >= 0),
  currentCost: orderCostDecimalSchema.refine((value) => Number(value) >= 0),
  amount: orderCostDecimalSchema.refine(
    (value) => Number(value) > 0 && Number(value) !== 1,
    "Catalog estimates require a positive, non-placeholder amount"
  ),
  catalog: z.object({
    source: z.literal("magento_catalog"),
    field: z.enum(["cost", "cost_it"]),
    storeId: z.literal("0"),
    amount: z.string().regex(/^\d{1,16}(?:\.\d{1,6})?$/),
    observedAt: sourceTime,
  }),
  approvalReference: z.string().min(10).max(2000),
})
export type OrderCatalogEstimate = z.infer<typeof orderCatalogEstimateSchema>

const approvalReference = z.string().min(10).max(2000)
const evidenceHash = z.string().regex(/^[a-f0-9]{64}$/)
export const orderPlacementAdjudicationSchema = z.object({
  orderId: z.string().min(1).max(200),
  orderEntityId: sourceId,
  sourceHash: evidenceHash,
  basis: z.enum([
    "invoice_original",
    "order_original_pending_invoice",
    "order_original_simple_pending_invoice",
  ]),
  approvalReference,
})
export type OrderPlacementAdjudication = z.infer<typeof orderPlacementAdjudicationSchema>
export const orderReplacementReviewSchema = z.object({
  orderId: z.string().min(1).max(200),
  selectedEntityId: sourceId,
  selectionVersion: z.number().int().nonnegative(),
  localFactHash: evidenceHash,
  mode: z.enum(["child_only", "parent_with_additional_cost"]),
  relation: z.enum(["parent_forward", "same_marketplace_order"]),
  parent: orderCostSourceSchema,
  child: orderCostSourceSchema,
  approvalReference,
  supersedesAuthorityHash: evidenceHash.optional(),
})
export type OrderReplacementReview = z.infer<typeof orderReplacementReviewSchema>
export const orderFinancialExclusionSchema = z.object({
  orderId: z.string().min(1).max(200),
  selectedEntityId: sourceId,
  selectionVersion: z.number().int().nonnegative(),
  localFactHash: evidenceHash,
  sourceHash: evidenceHash,
  basis: z.literal("canceled_marketplace_empty_basket"),
  approvalReference,
  supersedesAuthorityHash: evidenceHash.optional(),
})
export type OrderFinancialExclusion = z.infer<typeof orderFinancialExclusionSchema>

export const orderCostVerificationSchema = z
  .object({
    basis: z.literal(PNL_SOURCE_BASIS.order),
    source: z.literal(ORDER_COST_SOURCE),
    store: z.string().min(1),
    sourceStoreId: sourceId,
    from: sourceTime,
    to: sourceTime,
    currency: z.string().regex(/^[A-Z]{3}$/),
    taxBasis: z.literal("net"),
    unitBasis: z.literal("per_simple_unit"),
    placementBasis: z.literal("sum_original_ordered_simple_units"),
    reference: z.string().min(10).max(2000),
    retentionAssessment: z.string().min(10).max(4000),
    writePathAssessment: z.string().min(10).max(4000),
    storeCostAssessment: z.string().min(10).max(4000),
    // Exact documented corrections only. A broad September date is not evidence.
    corrections: z
      .array(
        z.object({
          orderEntityId: sourceId,
          itemId: sourceId,
          oldCost: orderCostDecimalSchema,
          newCost: orderCostDecimalSchema,
          createdAt: sourceTime,
          reference: z.string().min(10).max(2000),
        })
      )
      .max(1000),
    placementAdjudications: z.array(orderPlacementAdjudicationSchema).max(100).optional(),
    replacementReviews: z.array(orderReplacementReviewSchema).max(20).optional(),
    financialExclusions: z.array(orderFinancialExclusionSchema).max(20).optional(),
    // Optional so existing immutable verification records remain unchanged.
    catalogEstimates: z
      .array(orderCatalogEstimateSchema)
      .max(100)
      .refine(
        (values) =>
          new Set(values.map((value) => `${value.orderEntityId}:${value.itemId}`)).size ===
          values.length,
        "Catalog estimates must name distinct order items"
      )
      .optional(),
  })
  .refine((value) => value.from <= value.to, "Verification range must be ordered")
  .refine((value) => {
    const placements = value.placementAdjudications ?? []
    const reviews = value.replacementReviews ?? []
    const exclusions = value.financialExclusions ?? []
    return (
      new Set(placements.map((entry) => entry.orderEntityId)).size === placements.length &&
      new Set(reviews.map((entry) => entry.orderId)).size === reviews.length &&
      new Set(reviews.flatMap((entry) => [entry.parent.order.entityId, entry.child.order.entityId]))
        .size ===
        reviews.length * 2 &&
      new Set(exclusions.map((entry) => entry.orderId)).size === exclusions.length &&
      !exclusions.some((entry) => reviews.some((review) => review.orderId === entry.orderId)) &&
      new TextEncoder().encode(JSON.stringify(reviews)).byteLength <= 2 * 1024 * 1024
    )
  }, "Adjudications require distinct identities and at most 2 MiB of replacement evidence")
export type OrderCostVerification = z.infer<typeof orderCostVerificationSchema>
